// src/components/card/ArchetypeCard.tsx
import { memo, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Play } from "lucide-react";
import { ArchetypeId, ArchetypeConfig } from "../../constants/archetypes";
import { useResponsiveValue } from "../../hooks/useResponsive";
import { tiktokPixelService } from "../../services/tiktok-pixel.service";

interface ArchetypeCardProps {
  arch: ArchetypeId;
  config: ArchetypeConfig;
  theme: {
    primary: string;
    secondary: string;
    accent: string;
    glow: string;
    bg: string;
    activeBg: string;
    border: string;
    gradient: string;
  };
  index: number;
  activeIndex: number;
  setActive: () => void;
  xOffset: number;
  isMobile: boolean;
}

const ANIMATION_CONFIG = {
  spring: { type: "spring", damping: 25, stiffness: 120 },
  opacity: { duration: 0.4 },
} as const;

const ArchetypeCard = memo(function ArchetypeCard({
  arch,
  config,
  theme,
  index,
  activeIndex,
  setActive,
  xOffset,
  isMobile,
}: ArchetypeCardProps) {
  const navigate = useNavigate();
  const isActive = activeIndex === index;
  const firstName = config.name.split(" ")[0];

  // Fixed: useResponsiveValue now takes a tuple
  const glowBlur = useResponsiveValue(["blur-[60px]", "blur-[100px]", "blur-[160px]"]);
  const smallGlowBlur = useResponsiveValue(["blur-[40px]", "blur-[80px]", "blur-[120px]"]);

  // Memoized position calculations
  const positionStyle = useMemo(() => {
    if (!isMobile) {
      return { x: `${xOffset}vw`, zIndex: 50 - index, top: 0, left: 0 };
    }

    const yOffset = (() => {
      if (index === 0) return activeIndex >= 1 ? "-50vh" : "0vh";
      if (index === 1) return activeIndex === 2 ? "-35vh" : "15vh";
      return "30vh";
    })();

    return { y: yOffset, zIndex: 60 - index * 10 };
  }, [isMobile, index, activeIndex, xOffset]);

  // Navigation handlers
  const handleCreate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      tiktokPixelService.trackAddToCart(
        {
          content_id: arch,
          content_type: "product",
          content_name: `${config.name} Experience`,
        },
        0,
        "KES",
        `addtocart:archetype:${arch}`
      );

      navigate(`/create?archetype=${arch}`);
    },
    [navigate, arch, config.name]
  );

  const handlePreview = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      tiktokPixelService.trackViewContent(
        {
          content_id: arch,
          content_type: "product",
          content_name: `${config.name} Preview`,
        },
        0,
        "KES",
        `viewcontent:archetype_preview:${arch}`
      );

      navigate(`/preview/archetype/${arch}`);
    },
    [navigate, arch, config.name]
  );

  const handleCardClick = useCallback(() => {
    if (!isActive) setActive();
  }, [isActive, setActive]);

  return (
    <motion.div
      initial={false}
      animate={positionStyle}
      transition={ANIMATION_CONFIG.spring}
      className={`absolute ${
        !isMobile
          ? "top-0 h-screen w-[60vw] shadow-[40px_0_100px_rgba(0,0,0,0.9)]"
          : "w-full h-[70vh] top-0 left-0"
      }`}
      style={{ left: 0, willChange: "transform" }}
    >
      <div
        onClick={handleCardClick}
        className={`relative w-full h-full p-8 md:p-20 flex flex-col justify-center cursor-pointer transition-colors duration-700 backdrop-blur-3xl border-r
          ${isActive ? theme.activeBg : theme.bg}
          ${isActive ? theme.border : "border-white/10"}
          ${isMobile ? "border-r-0 border-b" : ""}
        `}
      >
        {/* Background gradient */}
        <div
          className={`absolute inset-0 bg-gradient-to-b ${theme.gradient} to-transparent opacity-30 pointer-events-none`}
        />

        {/* Desktop Label */}
        {!isMobile && (
          <div className="absolute top-24 md:top-32 right-6 md:right-12 flex flex-col items-center gap-6 z-20">
            <span
              className={`text-[10px] md:text-[11px] font-black uppercase tracking-[0.6em] [writing-mode:vertical-lr] transition-colors duration-500 ${
                isActive ? "text-white" : "text-white/20"
              }`}
            >
              Intentional Expression
            </span>

            <div className="relative flex items-center">
              {!isActive && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="absolute right-full -mt-40 whitespace-nowrap text-2xl md:text-4xl font-black uppercase tracking-[0.3em] text-white/60 [writing-mode:vertical-lr] rotate-180 drop-shadow-2xl mr-10"
                >
                  {firstName}
                </motion.span>
              )}

              <div className="relative w-[1.5px] h-16 md:h-24 overflow-hidden">
                <div className={`absolute inset-0 ${isActive ? "bg-white/40" : "bg-white/10"}`} />
                <motion.div
                  animate={{ y: ["-100%", "100%"] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 bg-gradient-to-b from-transparent via-white to-transparent h-1/2"
                />
              </div>
            </div>

            <span
              className={`text-2xl md:text-4xl font-light tracking-tighter transition-colors duration-500 ${
                isActive ? "text-white" : "text-white/20"
              }`}
            >
              0{index + 1}
            </span>
          </div>
        )}

        {/* Mobile Label */}
        {isMobile && (
          <div className="absolute bottom-6 left-6 right-6 flex justify-between items-center z-20">
            <span className="text-[10px] font-black uppercase tracking-[0.6em] text-white/40">
              Intentional Expression
            </span>
            <div className="flex items-center gap-4">
              <span className="text-2xl font-light tracking-tighter text-white/40">
                0{index + 1}
              </span>
              {!isActive && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-xl font-black uppercase tracking-[0.2em] text-white/40"
                >
                  {firstName}
                </motion.span>
              )}
            </div>
          </div>
        )}

        {/* Hero Content */}
        <div className={`relative z-10 space-y-6 md:space-y-8 ${isMobile ? "pb-16" : ""}`}>
          <motion.div
            animate={{
              opacity: isActive ? 1 : 0.3,
              x: isActive ? 0 : -30,
              filter: isActive ? "blur(0px)" : "blur(4px)",
            }}
            transition={ANIMATION_CONFIG.opacity}
            className="space-y-4"
          >
            <h2 className="text-5xl mt-[8vh] md:mt-[0] sm:text-6xl md:text-7xl lg:text-[80px] font-medium tracking-tighter uppercase leading-[0.85]">
              {firstName} <br />
              <span className="text-transparent" style={{ WebkitTextStroke: "1px white" }}>
                {config.name.split(" ")[1] || firstName}
              </span>
            </h2>
            <p className="max-w-xs md:max-w-md text-[10px] md:text-[11px] font-medium uppercase tracking-[0.3em] leading-relaxed opacity-70">
              {config.description}
            </p>
          </motion.div>

          {/* Buttons */}
          <div
            className={`flex flex-wrap gap-4 pt-4 md:pt-8 transition-opacity duration-300 ${
              isActive ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <button
              onClick={handleCreate}
              className="group flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full font-bold uppercase tracking-tighter text-[10px] shadow-[0_10px_30px_rgba(255,255,255,0.2)] hover:scale-105 transition-all"
            >
              Create This Experience
              <ChevronRight size={14} />
            </button>
            <button
              onClick={handlePreview}
              className="flex items-center gap-3 border border-white/20 bg-white/5 px-8 py-4 rounded-full font-bold uppercase tracking-tighter text-[10px]"
            >
              View Preview
              <Play size={10} fill="white" />
            </button>
          </div>
        </div>

        {/* Glow Effects */}
        <>
          <div
            className={`absolute bottom-[-15%] left-[-15%] w-[70%] h-[70%] ${glowBlur} rounded-full transition-opacity duration-1000 pointer-events-none ${
              isActive ? "opacity-40" : "opacity-5"
            } ${theme.glow}`}
          />
          <div
            className={`absolute top-[-10%] right-[-10%] w-[40%] h-[40%] ${smallGlowBlur} rounded-full transition-opacity duration-1000 pointer-events-none ${
              isActive ? "opacity-20" : "opacity-0"
            } ${theme.glow}`}
          />
        </>
      </div>
    </motion.div>
  );
});

export default ArchetypeCard;
